import { useFleetMachineAccountData } from '../../hooks/useFleetMachineAcountData';
import { useDateRange } from 'components';
import { parseISO, format } from 'date-fns';
import { calculateDataKeyTotals } from 'common/helpers/dataCounters';

import { useGetAlarmsForIntensifiersQuery } from './machineHealthApis';
import { useParams } from 'react-router-dom';

// 3rd party libraries
export interface UseAlarmsForIntensifierApiDataProps {
  cycles?: number;
  alarms_details?: {
    alarm_category: string;
    alarm_description: string;
    alarm_hpu_value: string;
    alarm_id: string;
    intensifier: string;
    total_alarm_count?: number;
  }[];
  tableData?: {
    scope?: string;
    alarm_category: string;
    alarm_description: string;
    alarm_hpu_value: string;
    alarm_id: string;
    intensifier: string;
    occurences_ratio: number;
    occurences_percentage: number;
    lineChartKey: string;
    occurences: number;
    total_alarm_count?: number;
  }[];
}

export interface UseAlarmsForIntensifierApiDataReturnProps
  extends UseAlarmsForIntensifierApiDataProps {
  isLoading?: boolean;
  hasError?: string;
  hasMessage?: string;
  total_alarm_count?: number;
  total_cycles?: number;
}

export interface UseAlarmsForIntensifierApiProps {
  startTime?: string;
  endTime?: string;
  machineId?: string;
}

export const useAlarmsForIntensifierApi = (
  props?: UseAlarmsForIntensifierApiProps
): UseAlarmsForIntensifierApiDataReturnProps => {
  const { isoDateRange } = useDateRange();

  let startTime = props?.startTime;
  let endTime = props?.endTime;
  let machineId = props?.machineId;

  if (!startTime || !endTime || !machineId) {
    startTime = isoDateRange.startTime;
    endTime = isoDateRange.endTime;
    machineId =
      useFleetMachineAccountData().machineId || useParams<{ machineId: string }>().machineId;
  }

  if (!machineId) return { hasError: 'missing machine id' };

  // Format it in the desired format
  const formattedTimestamp = format(parseISO(startTime), 'yyyy-MM-dd HH:mm:ss.SSS');
  const formattedEndstamp = format(parseISO(endTime), 'yyyy-MM-dd HH:mm:ss.SSS');

  const apiCall = useGetAlarmsForIntensifiersQuery({
    machineId,
    startTime: formattedTimestamp,
    endTime: formattedEndstamp
  });

  let total_cycles = 0;
  let total_alarm_count = 0;
  let tableData = undefined;
  // get the total number of alarms
  let alarmCount = 0;
  // process api call and return tableData
  if (apiCall?.data) {
    // break up api call and set types
    const { cycles, alarms_details } = apiCall.data as UseAlarmsForIntensifierApiDataProps;

    // to avoid errors
    if (alarms_details && cycles) {
      total_alarm_count = alarms_details.length;
      total_cycles = cycles;
      let groupedAlarms: Record<string, Record<string, unknown>> = {};
      // get the alarm totals return in the following format:
      // { [id]: { count: number } }
      const alarmTotals = calculateDataKeyTotals(alarms_details, 'alarm_id');
      // get the total number of alarms for this period
      alarmCount = alarms_details.length;
      // loop through the alarm details to build tableData
      alarms_details?.forEach((alarm) => {
        // this is all for typescript
        const id = alarm['alarm_id'] as string;
        const typedValObj = alarmTotals[id] as { count: number };
        // the number of times this alarm has occured
        const count = typedValObj.count;

        if (!groupedAlarms[id]) groupedAlarms = { ...groupedAlarms, [id]: {} };
        // need to break down a string for better formatting
        const [skidNum, intensifierNum] = (alarm['intensifier'] as string).split(' ')[1].split('.');
        // need to add a key that correponds to the line chart data
        // in order to show/hide
        const lineChartKey = `intensifier_utilization_${skidNum}_${intensifierNum}`;

        groupedAlarms[id] = {
          ...groupedAlarms[id],
          // the ratio of this alarm to the total number of alarms
          occurences_ratio: (count / alarmCount) * 100,
          occurences: count,
          total_alarm_count: alarms_details.length,
          // the ratio of this alarm to total cycles
          occurences_percentage: (count / cycles) * 100,
          lineChartKey,
          ...alarm
        };
      });
      // return the tableData as array
      tableData = Object.values(groupedAlarms) as UseAlarmsForIntensifierApiDataProps['tableData'];
      //sort by occurences count
      tableData = tableData?.sort((a, b) => (a.occurences < b.occurences ? 1 : -1));
    }
  }

  return { ...apiCall, tableData, total_alarm_count, total_cycles };
};
